import { Edge } from 'reactflow';
import { AppNode } from '../types';

const DEFAULT_W = 220;
const DEFAULT_H = 100;
const MIN_DELTA = 20;    // deadband on each axis to prevent flicker near zero offset

interface Center { x: number; y: number; }

function worldCenter(node: AppNode, byId: Map<string, AppNode>): Center {
  const w = node.width ?? DEFAULT_W;
  const h = node.height ?? DEFAULT_H;
  let x = node.position.x + w / 2;
  let y = node.position.y + h / 2;
  if (node.parentNode) {
    const parent = byId.get(node.parentNode);
    if (parent) {
      x += parent.position.x;
      y += parent.position.y;
    }
  }
  return { x, y };
}

function isStartNode(node: AppNode): boolean {
  return !!node.type?.includes('StartEvent');
}

function isBoundaryNode(node: AppNode): boolean {
  return !!node.parentNode;
}

export function resolveTargetHandles(nodes: AppNode[], edges: Edge[]): Edge[] {
  const byId = new Map<string, AppNode>();
  for (const n of nodes) byId.set(n.id, n);

  let anyChanged = false;
  const out = edges.map((edge) => {
    const src = byId.get(edge.source);
    const tgt = byId.get(edge.target);
    if (!src || !tgt) return edge;

    // Target is a start event: has no target handle — leave untouched
    if (isStartNode(tgt)) return edge;

    // Target is a boundary event: only one (Top) target handle, unnamed — leave untouched
    if (isBoundaryNode(tgt)) return edge;

    const sc = worldCenter(src, byId);
    const tc = worldCenter(tgt, byId);
    const dx = tc.x - sc.x;
    const dy = tc.y - sc.y;

    // Decide by which side of the target the source lies on:
    //   source-left  (dx > 0)  → Left handle is natural
    //   source-above (dy > 0)  → Top handle is natural
    // When both hold (source is above-left), pick the dominant axis so a
    // mostly-horizontal diagonal still uses Left, mostly-vertical uses Top.
    // When neither holds (source below/right), fall back to Left.
    const srcLeftOfTgt = dx > MIN_DELTA;
    const srcAboveTgt = dy > MIN_DELTA;

    let desired: 't-left' | 't-top';
    if (srcLeftOfTgt && srcAboveTgt) {
      desired = Math.abs(dx) >= Math.abs(dy) ? 't-left' : 't-top';
    } else if (srcAboveTgt) {
      desired = 't-top';
    } else {
      desired = 't-left';
    }

    if (edge.targetHandle === desired) return edge;
    anyChanged = true;
    return { ...edge, targetHandle: desired };
  });

  return anyChanged ? out : edges;
}
